import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { Subject, forkJoin } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { Profile } from "./shared/models/profile";

import { PractitionerService } from "./shared/services/practitioner.service";
import { EligibleExam } from "./shared/models/eligibleExam";
import { ICredential } from "../shared/models/credential";
import { UserService } from "../shared/services/user.service";
import { ViewStateService } from "../shared/services/view-state.service";
import { PractitionerMocksService } from "./../shared/services/mocks/practitioner-mocks.service";

@Component({
  selector: "app-practitioner",
  templateUrl: "./practitioner.component.html",
})
export class PractitionerComponent implements OnInit, OnDestroy {
  personId: string;
  credentials: Array<ICredential>;
  eligibleExams: Array<EligibleExam>;
  isCredentialed: boolean;
  firstName: string;
  lastName: string;
  middleName: string;
  suffix: string;
  profile: Profile;
  isContactFlagged = false;
  isBadPayment = false;
  isScheduledExams = false;
  revokedCRT = false;
  provisional = false;
  showBadge = false;
  isShowCCDown = false;
  isMaintenance = false;
  isUnderNBRCJE = false;
  private destroy$ = new Subject<void>();

  get isLoading(): boolean {
    return this.viewState.CurrentViewState.isLoading;
  }

  constructor(
    private userService: UserService,
    private viewState: ViewStateService,
    private router: Router,
    private practitionerMockService: PractitionerMocksService,
    private practitionerService: PractitionerService
  ) {
    this.isCredentialed = this.userService.CurrentUser.credentialHolder;
    this.isUnderNBRCJE = this.userService.CurrentUser.underNBRCJE;
    this.provisional = this.userService.CurrentUser.provisional;
    this.isShowCCDown = (<any>window).isShowCCDown;
    this.isMaintenance = (<any>window).isMaintenance;
    this.credentials = [];
    this.eligibleExams = [];
    this.userService.user$
      .pipe(
        takeUntil(this.destroy$),
        filter((x) => x !== null)
      )
      .subscribe((user) => {
        this.firstName = user.firstName;
        this.lastName = user.lastName;
        this.middleName = user.middleName;
        this.suffix = user.suffix;
        this.revokedCRT = user.revokedCRT;
      });
  }

  ngOnInit() {
    this.viewState.startLoading();
    this.personId = this.userService.CurrentUser.personId;
    this.retrieveUserCredentialsAndExams();
  }

  test() {
    this.router.navigate(["/purchase/products"], {
      queryParams: { recert: "true" },
    });
  }

  onScheduleExamClick(url: string) {
    window.open(url, "_blank");
  }

  holdsRRT() {
    const testVal = this.credentials.filter((p) => p.credential === "RRT");
    if (testVal.length === 0) {return false;}
    else {return true;}
  }

  isProvisional(): boolean {
    let retval = false;
    if (this.provisional) {
      retval = true;
    } else if (
      this.profile &&
      this.profile.ccpStatus &&
      this.profile.ccpStatus.provisional
    ) {
      retval = true;
    }
    return retval;
  }

  needsBaseCredential(testCred) {
    if (testCred === "ACCS" || testCred === "NPS" || testCred === "SDS") {
      return true;
    }
    return false;
  }

  isExpired(expirationDate) {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    if (expirationDate !== null && new Date(expirationDate) < currentDate) {
      return true;
    }
    return false;
  }

  private retrieveUserCredentialsAndExams() {
    forkJoin([
      this.practitionerService.getProfile(this.personId),
      this.practitionerService.getCredentials(this.personId),
      this.practitionerService.getEligibleExams(this.personId),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.profile = res[0] ? res[0] : [];
        this.isContactFlagged = this.profile.contactNBRC;
        this.isBadPayment = this.profile.badPayment;
        this.isScheduledExams = this.profile.scheduledExams;
        this.showBadge = this.profile.showBadge;
        this.determineIfBadAddress(res[0]);
        this.determineIfReviewDemographic(res[0]);
        this.credentials = res[1] ? res[1] : [];
        this.eligibleExams = res[2] ? res[2] : [];
        this.viewState.stopLoading();
      });
  }

  altCredentialDate(cert, altTextDate: string) {
    const compareDate = cert.certificationDate.substring(0, 10);
    return compareDate === altTextDate;
  }

  determineIfBadAddress(profile: any) {
    if (profile.badAddress || profile.badEmail || profile.reviewProfile)
      {this.router.navigate(["/practitioner/profile/edit"]);}
  }

  determineIfReviewDemographic(profile: any) {
    //if they need to review address that takes precendent
    if (profile.reviewDemographics && !(profile.badAddress || profile.badEmail || profile.reviewProfile))
       {this.router.navigate(["/practitioner/demographic/edit"]);}
  }

  showBadgePage() {
    let retval: boolean;
    if (
      this.showBadge &&
      !this.revokedCRT &&
      !this.provisional &&
      !this.isProvisional()
    )
      {retval = true;}
    return retval;
  }

  hasFreeExam(): boolean {
    if (
      this.eligibleExams &&
      this.eligibleExams.findIndex(
        (p) =>
          p.cost === 0 &&
          (p.applicationStatus === null || p.applicationStatus === "INCMP")
      ) > -1
    )
      {return true;}
    else {return false;}
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
